import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { loadErrorMessages } from '@apollo/client/dev';
import { ConsentTypes } from '@credit-acceptance/shareable-utilities';
import { adobeAnalytics } from 'adobe-analytics';
import {
  CollectMetricsMutationService,
  ConsentTextInfoQueryService,
  EMetricsTypes,
} from 'graphql-client';
import {
  BehaviorSubject,
  catchError,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  takeUntil,
  tap,
  zip,
} from 'rxjs';

import { GET_PREQUALIFIED_URI } from '../../assets/constants';
import { IdentificationComponent } from '../identification/identification.component';
import { SSNError } from '../interfaces/common';
import { AWSService } from '../services/aws.service';
import { LocalService } from '../services/local.service';
import { PrequalService } from '../services/prequal.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { UTMModel } from '../shared/models/utmModel';

window.location.href.includes('localhost') && loadErrorMessages();

const initMinutesLeft = 10;
const initSecondsLeft = 0;

@Component({
  selector: 'capq-main-container',
  templateUrl: './main-container.component.html',
  styleUrls: ['./main-container.component.scss'],
})
export class MainContainerComponent
  extends Unsubscribe
  implements OnInit, OnDestroy
{
  sessionRemainingTime = 30 * 60;
  minutesLeft = initMinutesLeft;
  secondsLeft = initSecondsLeft;
  remainingTimeInterval: ReturnType<typeof setInterval> | null = null;
  extendedSessionLength = 10 * 60;
  showPopup = false;
  forceReload = false;
  adobeScriptLoaded = false;
  adobeLoadingTime = 0;
  submissionFailureReason = '';
  preventDefaultOnWindowUnload = false;
  errorMessage: string = '';
  errorType: string = '';

  activeStep$: Observable<number>;
  activeStepUpdate = new Subject<number>();
  sessionExpirationTime$ = new BehaviorSubject<string>('');
  private readonly allConsentsGiven = new BehaviorSubject({
    identification: false,
    summary: false,
  });
  allConsentsGiven$ = this.allConsentsGiven
    .asObservable()
    .pipe(map((consents) => consents.identification && consents.summary));

  @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
    if (this.preventDefaultOnWindowUnload && !this.forceReload) {
      event.preventDefault();
    }
  }

  @ViewChild('identification')
  identificationComponent!: IdentificationComponent;

  constructor(
    readonly localService: LocalService,
    private readonly prequalService: PrequalService,
    private readonly awsService: AWSService,
    private readonly collectMetricsService: CollectMetricsMutationService,
    private readonly metaService: Meta,
    private readonly titleService: Title,
    private readonly consentTextInfoQueryService: ConsentTextInfoQueryService
  ) {
    super();

    this.activeStep$ =
      this.retrieveRequiredAppDataAndSetupActiveStepObservable();

    //UTM code
    adobeAnalytics.init((val: boolean, lt: number) => {
      this.adobeLoadingTime = lt;
      this.collectMetricsService
        .mutate({
          metrics: {
            metricsType: EMetricsTypes.Allt,
            value: lt.toString(),
          },
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe();
      this.adobeScriptLoaded = val;
    });

    let userSourceURL = '';
    if (window.location.href.toLowerCase().includes('utm_source')) {
      userSourceURL = window.location.href;
    }

    let sourceStoredInLocal: UTMModel[] = JSON.parse(
      localStorage.getItem('userSource') || '[]'
    );

    if (sourceStoredInLocal.length) {
      const diffInDays = localService.getDiffInDays(
        sourceStoredInLocal[0]?.time
      );
      if (diffInDays > 270) {
        sourceStoredInLocal = [];
        localService.setData('userSource', JSON.stringify(sourceStoredInLocal));
      }
    }

    const encodeURl = btoa(userSourceURL);

    if (userSourceURL) {
      const currentTime = new Date().toISOString();
      sourceStoredInLocal.push({
        url: encodeURl,
        time: currentTime,
        updated: true,
      });

      localService.setData('userSource', JSON.stringify(sourceStoredInLocal));
    }
    //UTM code
  }

  ngOnInit(): void {
    // Set up the session expiration timer
    this.remainingTimeInterval = setInterval(() => {
      this.sessionRemainingTime -= 1;
      if (!this.sessionRemainingTime) {
        // no session time left, reset
        this.remainingTimeInterval && clearInterval(this.remainingTimeInterval);
        this.reload();
      }
      if (this.sessionRemainingTime <= 10 * 60) {
        this.minutesLeft = Math.floor(this.sessionRemainingTime / 60);
        this.secondsLeft = this.sessionRemainingTime % 60;

        // generate session expiration time string for display
        const minutesLeft = this.minutesLeft
          ? `${this.minutesLeft} minute${this.minutesLeft > 1 ? 's' : ''}`
          : '';
        const secondsLeft = `${this.secondsLeft} second${
          this.secondsLeft > 1 ? 's' : ''
        }`;
        this.sessionExpirationTime$.next(`${minutesLeft} ${secondsLeft}`);
        this.showPopup = true;
      }
    }, 1000);
  }

  retrieveRequiredAppDataAndSetupActiveStepObservable() {
    return merge(
      this.prequalService.errorTriggered.pipe(map(() => -1)),
      zip(
        // fetch all required data the app needs before we can allow the user to begin interactions
        this.consentTextInfoQueryService.fetch({
          consentType: ConsentTypes.EMAIL_DISCLOSURE,
        }),
        this.consentTextInfoQueryService.fetch({
          consentType: ConsentTypes.PRODUCT_TERMS,
        }),
        this.awsService
          .getSEOData()
          .pipe(tap((data) => this.setupAppSEOData(data))),
        this.awsService.getPrequalifiedData()
      ).pipe(map(() => 1)),
      this.activeStepUpdate
    ).pipe(
      tap((activeStep) => {
        this.preventDefaultOnWindowUnload = activeStep < 7;
      }),
      startWith(0),
      catchError((error) => {
        console.error(`Failed to load required app data due to: ${error}`);
        return of(-1);
      })
    );
  }

  setupAppSEOData(SEOData: any) {
    const prequalifiedSEOData = SEOData[GET_PREQUALIFIED_URI];
    this.metaService.addTag({
      content: 'text/html; charset=UTF-8',
      httpEquiv: 'content-type',
    });
    this.metaService.addTag({
      name: 'facebook-domain-verification',
      content: 'mvxyneyuqg2hec8wzlwa6aluhfbrya',
    });
    this.metaService.addTag({
      content: prequalifiedSEOData.description,
      name: 'description',
    });
    this.titleService.setTitle(prequalifiedSEOData.title);
    this.metaService.addTag({
      name: 'canonical',
      content: window.location.href,
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.remainingTimeInterval && clearInterval(this.remainingTimeInterval);
  }

  extend() {
    this.showPopup = false;
    this.minutesLeft = initMinutesLeft;
    this.secondsLeft = initSecondsLeft;
    this.sessionRemainingTime += this.extendedSessionLength;
  }
  reload() {
    this.forceReload = true;
    window.location.reload();
  }

  updateActiveStep(step: number) {
    this.activeStepUpdate.next(step);
    this.errorMessage = '';
    this.errorType = '';
  }

  updateFailureReason(reason: string) {
    this.submissionFailureReason = reason;
  }

  updateConsent(type: 'identification' | 'summary', consent: boolean) {
    this.allConsentsGiven.next({
      ...this.allConsentsGiven.value,
      [type]: consent,
    });
  }

  updateSSNError(ssnError: SSNError) {
    this.errorMessage = ssnError.message;
    this.errorType = ssnError.type;
    setTimeout(() => {
      const element = document.getElementById('error-subsection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  }

  resetIdentificationConsent() {
    this.identificationComponent.resetConsentAgreement();
  }
}
